import type { RegisterFormSubmitFn } from '../../form/RegisterForm/RegisterFormBase/RegisterFormBase';

import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import useLocalStorageState from 'use-local-storage-state';

import { postRegister } from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import {
  generateRedirectUrl,
  getMaskedOrganizationName,
  getTestProps,
  sendEventAndClear,
} from '../../lib/helpers';

import SignUpInfo from '../../components/SignUpInfo/SignUpInfo';

import RegisterForm from '../../form/RegisterForm/RegisterForm';
import Authentication from '../../hoc/Authentication/Authentication';

const testId = 'register';

const Register = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [, , { removeItem: removeUser }] = useLocalStorageState('cms.user');

  const handlePost = useCallback<RegisterFormSubmitFn>(
    async (values) => {
      try {
        const { body, status, ok } = await postRegister({
          ...values,
          redirectUri: generateRedirectUrl('/register/activate'),
          plan: searchParams.has('plan') ? searchParams.get('plan') : '',
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('Global.CreatedAccount'), { duration: 10000 });

        sendEventAndClear(
          {
            event: 'sign_up',
            user_id: body.user_id,
            organization_id: body.organization_id,
            organization_name: getMaskedOrganizationName(
              body.organization_name,
            ),
            plan_id: body.plan_id,
            plan_name: body.plan_name,
            space_ids: body.space_ids,
          },
          {},
        );

        removeUser();
        navigate('/');
        return null;
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return {
            general: t('Form.CommunicationErrorMessage'),
          };
        }

        if (error.errors?.recaptchaToken) {
          toast.error(error.errors?.recaptchaToken?.[0]);
          return {
            general: error.errors?.recaptchaToken?.[0],
            ...error.errors,
          };
        }

        toast.error(error.message ? error.message : t('Form.ValidationError'));
        return {
          general: t('Form.ValidationError'),
          ...error.errors,
        };
      }
    },
    [navigate, removeUser, searchParams, t],
  );

  return (
    <>
      <title>{t('Global.RegisterToFlotiq')}</title>
      <Authentication
        authButtons={true}
        headerText={t('Global.CreateAccount')}
        paragraphText={
          <>
            {t('Global.StartForFree')}{' '}
            <span className="block text-sm lg:text-base mb-1 mt-6 lg:mt-4 2xl:mt-6">
              {t('Global.HaveAccount')}
              <Link to={`/${i18n.language}/login`} className="text-blue pl-1">
                {t('Global.LogIn')}
              </Link>
            </span>
          </>
        }
        infoSection={<SignUpInfo />}
        {...getTestProps(testId, 'authentication', 'testId')}
      >
        <RegisterForm
          onSubmit={handlePost}
          {...getTestProps(testId, 'form', 'testId')}
        />
      </Authentication>
    </>
  );
};

export default Register;
